import { Injectable, Inject, Optional } from '@angular/core';
import { ChatService, Participant, Message } from '@mitel/cloudlink-sdk/chat';
import { NotificationsService, Transports } from '@mitel/cloudlink-sdk/notifications';
import { AdminService, User, Contact } from '@mitel/cloudlink-sdk/admin';
import { PresenceService } from '@mitel/cloudlink-sdk/presence';
import { MediaService } from '@mitel/cloudlink-sdk/media';

import { TopicFocusedNotify } from '@mitel/cloudlink-sdk/notifications';
import { ReplaySubject, Subscription as rxjsSubscription, Subject, defer, fromEvent } from 'rxjs';
import { filter, map, retryWhen, takeUntil } from 'rxjs/operators';
import { SharedHelpers } from '../classes/sharedhelpers';
import { detect } from 'detect-browser';
import { Attachment, ParticipantTypingAction, Reaction, Translation, Conversation } from '@mitel/cloudlink-sdk/chat';
import { ChatLoggingService } from './chat-logging.service';
import { ChatDatabaseService } from './chat-database.service';
import { MessageEx } from '../classes/messageex';
import { version } from '../../version';

declare const Paho: any;

export interface ParticipantEx extends Participant {
  method: 'PUT' | 'POST' | 'DELETE';
}

export interface DeletedParticipant {
  userId: string;
  conversationId: string;
}

@Injectable({
  providedIn: 'root'
})
export class ChatCloudLinkService {
  public elasticDelay = 15000;
  public offlineLogin = false;

  public ready$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  // 24 hours expiry time
  private subscriptionExpiryTime = 86400000;
  // 1/4 of expiry time
  get subscriptionIntervalTime(): number {
    if (this.refreshTime) {
      return this.refreshTime;
    }
    return this.subscriptionExpiryTime / 4;
  }
  public accountId: string;

  public messageSubscriptionReady$: ReplaySubject<boolean> = new ReplaySubject(1);
  public subscriptionError$: ReplaySubject<any> = new ReplaySubject(1);

  public notify: NotificationsService;
  public presence: PresenceService;
  public chat: ChatService;
  public admin: AdminService;
  public media: MediaService;
  private suggestionNotify: TopicFocusedNotify<any>;
  public browser: any;
  public iOSVersion: number[] | null;

  private subUnsubscribe: Subject<void> = new Subject();
  private transport: Transports = 'websocket';
  private subIds = [];
  private subSubjects = new rxjsSubscription();
  public refreshInterval;
  private _isOffline = false;
  private _hostOffline = false;
  private onlineTimeout;
  public recentMessages: Message[] = [];
  public recentMessagesTimeouts: {
    messageId: string;
    timeout: any;
  }[] = [];
  public recentDeletedMessages: string[] = [];
  public logMessageEvents = false;
  public jsonMessagesAsSource = false;
  // Mirrors jsonMessagesAsSource: bypasses CloudLink login/notify entirely for ChatComponent's
  // standalone aiMode, which talks to MAIS's v1 API instead of the CloudLink SDK.
  public aiMode = false;

  public conversationsChanged$: Subject<Conversation> = new Subject<Conversation>();
  public conversationsDeleted$: Subject<string> = new Subject<string>();
  public participantsChanged$: Subject<ParticipantEx> = new Subject<ParticipantEx>();
  public participantsDeleted$: Subject<DeletedParticipant> = new Subject<DeletedParticipant>();
  public messagesChanged$: Subject<Message> = new Subject<Message>();
  public messagesDeleted$: Subject<string> = new Subject<string>();
  public attachmentsChanged$: Subject<Attachment> = new Subject<Attachment>();
  public attachmentsDeleted$: Subject<string> = new Subject<string>();
  public usersChanged$: Subject<User> = new Subject<User>();
  public getUser$: Subject<string> = new Subject<string>();
  public contactsChanged$: Subject<Contact> = new Subject<Contact>();
  public suggestionChanged$: Subject<any> = new Subject<any>();
  public typingChanged$: Subject<ParticipantTypingAction> = new Subject<ParticipantTypingAction>();
  public reactionsChanged$: Subject<Reaction> = new Subject<Reaction>();
  public reactionsDeleted$: Subject<string> = new Subject<string>();
  public translationsChanged$: Subject<Translation> = new Subject<Translation>();
  public online$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);
  public reloadConversation$: Subject<boolean> = new Subject<boolean>();
  public messageNotAddedToDb$: Subject<Message> = new Subject<Message>();

  constructor(
    private chatLoggingService: ChatLoggingService,
    private chatDatabaseService: ChatDatabaseService,
    @Inject('cloudlinkExpiryTime') @Optional() public expiryTime?: number,
    @Inject('cloudlinkRefreshTime') @Optional() public refreshTime?: number
  ) {
    fromEvent(window, 'online').subscribe(async () => {
      this.clearOnlineTimeout();
      //Delay on online logic because sometimes the browser's network isn't truly online immediately
      this.onlineTimeout = setTimeout(() => {
        this._isOffline = false;
        if (!this._hostOffline) {
          this.online$.next(true);
        }
      }, 5000);
    });
    fromEvent(window, 'offline').subscribe(() => {
      this.clearOnlineTimeout();
      this._isOffline = true;
      if (!this._hostOffline) {
        this.online$.next(false);
      }
    });

    this.browser = detect();
    this.iOSVersion = this.getIosVersion();
    this.chatLoggingService.info({
      logMessage: 'chat-cloudlink.service: Chat component version',
      logParameters: [version]
    });
    this.chatLoggingService.info({
      logMessage: 'chat-cloudlink.service: Chat component browser detected is',
      logParameters: [this.browser?.name, this.browser?.os, this.iOSVersion, navigator.platform, navigator.appVersion]
    });

    this.checkForNotify();

    if (expiryTime) {
      this.subscriptionExpiryTime = expiryTime;
    }
  }

  clearOnlineTimeout() {
    if (this.onlineTimeout) {
      clearTimeout(this.onlineTimeout);
      this.onlineTimeout = null;
    }
  }

  setHostOffline(offline) {
    if (offline !== this._hostOffline) {
      this.chatLoggingService.info({
        logMessage: 'chat-cloudlink.service.setHostOffline()',
        logParameters: [offline]
      });
      this._hostOffline = offline;
      if (!offline && !this._isOffline && window.navigator.onLine) {
        this.online$.next(true);
      } else if (offline && !this._isOffline && window.navigator.onLine) {
        this.online$.next(false);
      }
    }
  }

  public isOffline(): boolean {
    return this._isOffline || this._hostOffline || !window.navigator.onLine;
  }

  public async checkForNotify() {
    this.ready$ = new ReplaySubject<boolean>(1);
    var existingConfig = SharedHelpers.getSdkConfig();
    var notifyCount = 0;
    this.offlineLogin = false;

    this.chatLoggingService.info({
      logMessage: 'chat-cloudlink.service.checkForNotify() existingConfig',
      logParameters: [!!existingConfig]
    });

    var notifyInterval = setInterval(() => {
      this.chatLoggingService.info({
        logMessage: 'chat-cloudlink.service.checkForNotify() notifyInterval',
        logParameters: [!!existingConfig?.getMicroService?.(NotificationsService), this.isOffline()]
      });
      if (this.jsonMessagesAsSource || this.aiMode) {
        this.chatLoggingService.info({
          logMessage: 'chat-cloudlink.service.checkForNotify() jsonMessagesAsSource/aiMode enabled - set ready'
        });

        clearInterval(notifyInterval);
        this.ready$.next(true);
        this.messageSubscriptionReady$.next(true);
      } else if (this.chatDatabaseService.indexDbEnabled && this.isOffline()) {
        this.chatLoggingService.info({
          logMessage: 'chat-cloudlink.service.checkForNotify() Starting offline'
        });
        this.offlineLogin = true;
        clearInterval(notifyInterval);
        this.ready$.next(true);
      } else if (notifyCount === 10) {
        clearInterval(notifyInterval);
        this.chatLoggingService.error({
          logMessage: 'chat-cloudlink.service.checkForNotify() No notify configured',
          logParameters: [!!existingConfig?.getMicroService?.(NotificationsService), this.isOffline()]
        });
        this.ready$.error('chat-cloudlink.service.checkForNotify(): No notify configured');
      } else {
        if (!!existingConfig?.getMicroService?.(NotificationsService) && !this.isOffline()) {
          clearInterval(notifyInterval);
          this.initialize();
          this.refreshInterval = setInterval(() => {
            existingConfig
              .getAuthenticationService()
              .getToken()
              .then(token => {
                if (token) {
                  this.refreshSubscriptionsExpiries();
                }
              });
          }, this.subscriptionIntervalTime);
        } else {
          notifyCount++;
        }
      }
    }, 1000);
  }

  public initialize() {
    const existingConfig = SharedHelpers.getSdkConfig();

    if ((<any>window).cloudlink && (<any>window).cloudlink.Config && (<any>window).cloudlink.Config.global) {
      this.notify = existingConfig?.getMicroService?.((<any>window).cloudlink.notifications.NotificationsService);
      this.chat = existingConfig?.getMicroService?.((<any>window).cloudlink.chat.ChatService);
      this.presence = existingConfig?.getMicroService?.((<any>window).cloudlink.presence.PresenceService);
      this.admin = existingConfig?.getMicroService?.((<any>window).cloudlink.admin.AdminService);
      this.media = existingConfig?.getMicroService?.((<any>window).cloudlink.media.MediaService);
    } else if (!!existingConfig?.getMicroService?.(NotificationsService)) {
      this.notify = existingConfig?.getMicroService?.(NotificationsService);
      this.chat = existingConfig?.getMicroService?.(ChatService);
      this.presence = existingConfig?.getMicroService?.(PresenceService);
      this.admin = existingConfig?.getMicroService?.(AdminService);
      this.media = existingConfig?.getMicroService?.(MediaService);
    } else {
      this.notify = new NotificationsService({ config: existingConfig });
      this.chat = new ChatService({ config: existingConfig });
      this.presence = new PresenceService({ config: existingConfig });
      this.admin = new AdminService({ config: existingConfig });
      this.media = new MediaService({ config: existingConfig });
    }

    this.suggestionNotify = new TopicFocusedNotify({
      notify: this.notify,
      topic: 'interaction-suggestion'
    });

    this.subscribeToSubs();

    this.ready$.next(true);
  }

  public async subscribeToSubs(tryRefresh = false) {
    this.chatLoggingService.info({
      logMessage: 'chat-cloudlink.service.subscribeToSubs()',
      logParameters: [tryRefresh]
    });

    if (tryRefresh) {
      await this.refreshSubscriptionsExpiries(false);
    } else {
      if (this.subUnsubscribe) {
        this.subUnsubscribe.next();
        this.subUnsubscribe.complete();
        this.subUnsubscribe = new Subject();
      }

      this.subSubjects.unsubscribe();
      this.subSubjects = new rxjsSubscription();
      this.subIds = [];

      this.subSubjects.add(
        this.notify
          .asDiagnosticsObservable()
          .pipe(filter(message => message.isError))
          .subscribe(message => {
            this.chatLoggingService.error({
              logMessage: 'chat-cloudlink.service.initalize notify.asDiagnosticsObservable() error',
              logParameters: [message]
            });
          })
      );

      this.messageSubscriptionReady$ = new ReplaySubject(1);
      this.subscriptionError$.next(null);

      this.subscribeToChatNotifySubscriptions();
    }
  }

  public unsubscribe(): Promise<void> {
    // aiMode (or a session that never got past login) never calls initialize(), so notify is
    // never constructed - nothing was ever subscribed server-side, so there's nothing to tear
    // down.
    if (!this.notify) {
      return Promise.resolve();
    }
    return this.notify
      .unsubscribeDevice()
      .then(() => {
        this.chatLoggingService.info({
          logMessage: 'chat-cloudlink.service.unsubscribe()=> OK'
        });
      })
      .catch(ex => {
        this.chatLoggingService.error({
          logMessage: 'chat-cloudlink.service.unsubscribe() error',
          logParameters: [ex]
        });
      });
  }

  public reset() {
    this.ready$ = new ReplaySubject(1);
  }

  public sendMessagesChanged(message: MessageEx) {
    this.messagesChanged$.next(message);
  }

  public sendMessagesDeleted(messageId: string, deleteDatabaseMessage = false) {
    this.messagesDeleted$.next(messageId);
    if (deleteDatabaseMessage) {
      this.deleteDatabaseMessage(messageId);
    }
  }

  private refreshServiceRefs(): void {
    const existingConfig = SharedHelpers.getSdkConfig();
    if (!existingConfig) return;
    let freshNotify: NotificationsService;
    if ((<any>window).cloudlink?.Config?.global) {
      freshNotify = existingConfig.getMicroService?.((<any>window).cloudlink.notifications.NotificationsService);
    } else {
      freshNotify = existingConfig.getMicroService?.(NotificationsService);
    }
    if (freshNotify && freshNotify !== this.notify) {
      // createNotify() ran after initialize() captured stale refs — rebuild with the MQTT-configured service
      this.chatLoggingService.info({
        logMessage: `chat-cloudlink.service.refreshServiceRefs(): Updating service refs after refresh with transport ${this.transport}`
      });
      this.notify = freshNotify;
      this.chat = new ChatService({ config: existingConfig });
      this.admin = new AdminService({ config: existingConfig });
      this.presence = new PresenceService({ config: existingConfig });
      this.media = new MediaService({ config: existingConfig });
    }
  }

  private subscribeToChatNotifySubscriptions() {
    // this gives me all conversation changes... i.e. basic meta changes for adds/ edits / deletes
    // on all conversations that I'm part of.
    defer(() => {
      this.refreshServiceRefs();
      return this.chat.notifications().beginSubscribe().appendResource({ type: 'conversations' }).endSubscribe({
        transport: this.transport,
        expiryDateTime: this.getSubscriptionExpiry()
      });
    })
      .pipe(
        retryWhen(
          SharedHelpers.genericRetryStrategy(
            '[chat-cloudlink.service.subscribeToChatNotifySubscriptions()] - conversations',
            this.chatLoggingService
          )
        ),
        takeUntil(this.subUnsubscribe)
      )
      .toPromise()
      .then(subscription => {
        if (subscription) {
          if (this.subIds.indexOf(subscription.subscriptionId) === -1) {
            this.subIds.push(subscription.subscriptionId);
          }
          const changed$ = this.chat
            .notifications()
            .asObservable({
              subscription: subscription,
              resource: 'conversations'
            })
            .pipe(
              filter(publication => publication.method !== 'DELETE'),
              map(publication => publication.content)
            );

          this.subSubjects.add(
            changed$.subscribe((conversation: Conversation) => {
              if (conversation) {
                this.conversationsChanged$.next(conversation);
              }
            })
          );

          const deleted$ = this.chat
            .notifications()
            .asObservable({
              subscription: subscription,
              resource: 'conversations'
            })
            .pipe(
              filter(publication => publication.method === 'DELETE'),
              map(publication => publication.subject.split('/')?.pop())
            );

          this.subSubjects.add(
            deleted$.subscribe((resourceId: string) => {
              if (resourceId) {
                this.conversationsDeleted$.next(resourceId);
              }
            })
          );

          this.chatLoggingService.info({
            logMessage: `chat-cloudlink.service.subscribeConversation(): ${this.transport} conversations`
          });
        }
      })
      .catch(ex => {
        this.subscriptionError$.next('conversations');
        this.chatLoggingService.error({
          logMessage: `chat-cloudlink.service.subscribeConversations(): ${this.transport}.error`,
          logParameters: [ex]
        });
      });

    // participants added / removed to a conversation
    defer(() => {
      this.refreshServiceRefs();
      return this.chat
        .notifications()
        .beginSubscribe()
        .appendResource({ type: 'conversations' })
        .appendResource({ type: 'participants' })
        .endSubscribe({
          transport: this.transport,
          expiryDateTime: this.getSubscriptionExpiry()
        });
    })
      .pipe(
        retryWhen(
          SharedHelpers.genericRetryStrategy(
            '[chat-cloudlink.service.subscribeToChatNotifySubscriptions()] - conversations/participants',
            this.chatLoggingService
          )
        ),
        takeUntil(this.subUnsubscribe)
      )
      .toPromise()
      .then(subscription => {
        if (subscription) {
          if (this.subIds.indexOf(subscription.subscriptionId) === -1) {
            this.subIds.push(subscription.subscriptionId);
          }
          this.subSubjects.add(
            this.chat
              .notifications()
              .asObservable({
                subscription: subscription,
                resource: 'participants'
              })
              .subscribe(async (update: any) => {
                if ((update?.method === 'POST' || update?.method === 'PUT') && update?.content) {
                  let participant: ParticipantEx = update.content;
                  participant.method = update.method;
                  this.participantsChanged$.next(participant);
                  try {
                    let result = await this.chatDatabaseService.addParticipantIfConversationExists(participant);
                    if (result) {
                      this.getUser$.next(participant.userId);
                    }
                  } catch (ex) {
                    this.chatLoggingService.error({
                      logMessage: 'chat-cloudlink.service.subscribeParticipants(): Error adding participant to db',
                      logParameters: [participant.conversationId, participant.userId, ex]
                    });
                  }
                } else if (update?.method === 'DELETE') {
                  let convRegex = new RegExp('/conversations/(.*?)/');
                  let userRegex = new RegExp('/participants/(.*?$)');
                  let conversationMatches = update.subject.match(convRegex);
                  let userMatches = update.subject.match(userRegex);
                  if (conversationMatches?.length && conversationMatches[1] && userMatches?.length && userMatches[1]) {
                    this.participantsDeleted$.next({
                      conversationId: conversationMatches[1],
                      userId: userMatches[1]
                    });
                    try {
                      await this.chatDatabaseService.deleteParticipant(
                        this.accountId,
                        conversationMatches[1],
                        userMatches[1]
                      );
                    } catch (ex) {
                      this.chatLoggingService.error({
                        logMessage:
                          'chat-cloudlink.service.subscribeParticipants(): Error deleting participant from db',
                        logParameters: [userMatches[1], conversationMatches[1], ex]
                      });
                    }
                  } else {
                    this.chatLoggingService.error({
                      logMessage: 'chat-cloudlink.service.subscribeParticipants(): Regex failed for delete event',
                      logParameters: [update?.subject]
                    });
                  }
                }
              })
          );
          this.chatLoggingService.info({
            logMessage: `chat-cloudlink.service.subscribeParticipants(): ${this.transport} participants`
          });
        }
      })
      .catch(ex => {
        this.subscriptionError$.next('participants');
        this.chatLoggingService.error({
          logMessage: `chat-cloudlink.service.subscribeParticipants(): ${this.transport}.error`,
          logParameters: [ex]
        });
      });

    // messages and all replies as a sub resource
    // websocket for incognito. fcm doesnot work in incognito
    defer(() => {
      this.refreshServiceRefs();
      return this.chat
        .notifications()
        .beginSubscribe()
        .appendResource({ type: 'conversations' })
        .appendResource({ type: 'messages' })
        .endSubscribe({
          transport: this.transport,
          expiryDateTime: this.getSubscriptionExpiry()
        });
    })
      .pipe(
        retryWhen(
          SharedHelpers.genericRetryStrategy(
            '[chat-cloudlink.service.subscribeToChatNotifySubscriptions()] - conversations/messages',
            this.chatLoggingService
          )
        ),
        takeUntil(this.subUnsubscribe)
      )
      .toPromise()
      .then(subscription => {
        if (subscription) {
          if (this.subIds.indexOf(subscription.subscriptionId) === -1) {
            this.subIds.push(subscription.subscriptionId);
          }

          const changed$ = this.chat
            .notifications()
            .asObservable({
              subscription: subscription,
              resource: 'messages'
            })
            .pipe(
              filter(publication => publication.method !== 'DELETE'),
              map(publication => publication.content)
            );

          this.subSubjects.add(
            changed$.subscribe(async (message: Message) => {
              if (message) {
                try {
                  if (this.recentMessages.find(m => m.messageId === message.messageId)) {
                    Object.assign(
                      this.recentMessages.find(m => m.messageId === message.messageId),
                      message
                    );
                    let messageTimeout = this.recentMessagesTimeouts.find(m => m.messageId);
                    if (messageTimeout) {
                      clearTimeout(messageTimeout.timeout);
                      this.recentMessagesTimeouts = this.recentMessagesTimeouts.filter(
                        m => m.messageId !== message.messageId
                      );
                    }
                  } else {
                    //There's an eventually consistent issue with Elastic Search (more prevalent on public). We may not get the event right away, but
                    //if we do a GET on that same message, it won't exist in the Elastic DB yet. It can take up to the 10-11s from what we've seen.
                    //To solve this, we store it for 15 seconds and compare against this list in the fetchMessages in  the chat component.
                    this.recentMessages.push(message);
                  }
                  this.recentMessagesTimeouts.push({
                    messageId: message.messageId,
                    timeout: setTimeout(() => {
                      this.recentMessages = this.recentMessages.filter(m => m.messageId !== message.messageId);
                      this.recentMessagesTimeouts = this.recentMessagesTimeouts.filter(
                        m => m.messageId !== message.messageId
                      );
                    }, this.elasticDelay)
                  });
                } catch (ex) {
                  this.chatLoggingService.error({
                    logMessage: 'chat-cloudlink.service.subscribeMessages(): Error adding message to recent messages',
                    logParameters: [message.conversationId, message.messageId, ex]
                  });
                }
                if (this.logMessageEvents) {
                  this.chatLoggingService.info({
                    logMessage: 'chat-cloudlink.service.subscribeMessages(): Firing messagesChanged$',
                    logParameters: [message.conversationId, message.messageId]
                  });
                }
                this.messagesChanged$.next(message);
                try {
                  let result = await this.chatDatabaseService.addMessageIfConversationExists(message as MessageEx);
                  if (result === false) {
                    this.messageNotAddedToDb$.next(message);
                  }
                } catch (ex) {
                  this.chatLoggingService.error({
                    logMessage: 'chat-cloudlink.service.subscribeMessages(): Error adding message to db',
                    logParameters: [message.conversationId, message.messageId, ex]
                  });
                }
              }
            })
          );

          const deleted$ = this.chat
            .notifications()
            .asObservable({
              subscription: subscription,
              resource: 'messages'
            })
            .pipe(
              filter(publication => publication.method === 'DELETE'),
              map(publication => publication.subject.split('/')?.pop())
            );

          this.subSubjects.add(
            deleted$.subscribe(async (resourceId: string) => {
              this.chatLoggingService.info({
                logMessage: 'chat-cloudlink.service.deleted$.subscribe():',
                logParameters: [resourceId]
              });
              if (resourceId) {
                try {
                  //There's an eventually consistent issue with Elastic Search (more prevalent on public). We may not get the event right away, but
                  //if we do a GET on that same message, the delete may not be reflected in the Elastic DB yet.
                  //It can take up to the 10-11s from what we've seen.
                  //To solve this, we store it for 15 seconds and compare against this list in the fetchMessages in  the chat component.
                  if (this.recentMessages.find(m => m.messageId === resourceId)) {
                    this.recentMessages = this.recentMessages.filter(m => m.messageId !== resourceId);
                    let messageTimeout = this.recentMessagesTimeouts.find(m => m.messageId);
                    if (messageTimeout) {
                      clearTimeout(messageTimeout.timeout);
                      this.recentMessagesTimeouts = this.recentMessagesTimeouts.filter(m => m.messageId !== resourceId);
                    }
                  }
                  this.recentDeletedMessages.push(resourceId);
                  setTimeout(() => {
                    this.recentDeletedMessages = this.recentDeletedMessages.filter(d => d !== resourceId);
                  }, this.elasticDelay);
                } catch (ex) {
                  this.chatLoggingService.error({
                    logMessage: 'chat-cloudlink.service.subscribeMessages(): Error removing recent message',
                    logParameters: [resourceId, ex]
                  });
                }
                this.messagesDeleted$.next(resourceId);
                this.deleteDatabaseMessage(resourceId);
              }
            })
          );
          this.messageSubscriptionReady$.next(true);
          this.chatLoggingService.info({
            logMessage: `chat-cloudlink.service.subscribeMessages(): ${this.transport} messages`
          });
        }
      })
      .catch(ex => {
        this.messageSubscriptionReady$.next(false);
        this.subscriptionError$.next('messages');
        this.chatLoggingService.error({
          logMessage: `chat-cloudlink.service.subscribeMessages(): ${this.transport}.error`,
          logParameters: [ex]
        });
      });

    // attachments
    defer(() => {
      this.refreshServiceRefs();
      return this.chat
        .notifications()
        .beginSubscribe()
        .appendResource({ type: 'attachments', target: '[^/]+.*' })
        .endSubscribe({
          transport: this.transport,
          expiryDateTime: this.getSubscriptionExpiry()
        });
    })
      .pipe(
        retryWhen(
          SharedHelpers.genericRetryStrategy(
            '[chat-cloudlink.service.subscribeToChatNotifySubscriptions()] - attachments',
            this.chatLoggingService
          )
        ),
        takeUntil(this.subUnsubscribe)
      )
      .toPromise()
      .then(subscription => {
        if (subscription) {
          if (this.subIds.indexOf(subscription.subscriptionId) === -1) {
            this.subIds.push(subscription.subscriptionId);
          }

          const changed$ = this.chat
            .notifications()
            .asObservable({
              subscription: subscription,
              resource: 'attachments'
            })
            .pipe(
              filter(publication => publication.method !== 'DELETE'),
              map(publication => publication.content)
            );

          this.subSubjects.add(
            changed$.subscribe((attachment: Attachment) => {
              if (attachment) {
                this.attachmentsChanged$.next(attachment);
              }
            })
          );

          const deleted$ = this.chat
            .notifications()
            .asObservable({
              subscription: subscription,
              resource: 'attachments'
            })
            .pipe(
              filter(publication => publication.method === 'DELETE'),
              map(publication => publication.subject.split('/')?.pop())
            );

          this.subSubjects.add(
            deleted$.subscribe(async (resourceId: string) => {
              if (resourceId) {
                this.attachmentsDeleted$.next(resourceId);
              }
            })
          );
          this.chatLoggingService.info({
            logMessage: `chat-cloudlink.service.subscribeAttachments(): ${this.transport} attachments`
          });
        }
      })
      .catch(ex => {
        this.subscriptionError$.next('attachments');
        this.chatLoggingService.error({
          logMessage: `chat-cloudlink.service.subscribeAttachments(): ${this.transport}.error`,
          logParameters: [ex]
        });
      });

    defer(() => {
      this.refreshServiceRefs();
      return this.chat
        .notifications()
        .beginSubscribe()
        .appendResource({
          type: 'conversations',
          target: '[^/]+/(?:participants|messages)/[^/]+/(?:typing|reactions|translations).*'
        })
        .endSubscribe({
          transport: this.transport,
          expiryDateTime: this.getSubscriptionExpiry()
        });
    })
      .pipe(
        retryWhen(
          SharedHelpers.genericRetryStrategy(
            '[chat-cloudlink.service.subscribeToChatNotifySubscriptions()] - conversations/typing|reactions|translations',
            this.chatLoggingService
          )
        ),
        takeUntil(this.subUnsubscribe)
      )
      .toPromise()
      .then(subscription => {
        if (subscription) {
          if (this.subIds.indexOf(subscription.subscriptionId) === -1) {
            this.subIds.push(subscription.subscriptionId);
          }
          const changed$ = this.chat
            .notifications()
            .asObservable({ subscription: subscription })
            .pipe(
              filter(publication => publication.method !== 'DELETE'),
              map(publication => publication.content)
            );

          this.subSubjects.add(
            changed$.subscribe((resource: any) => {
              if (resource.languageCode) {
                this.translationsChanged$.next(resource);
              } else if (resource.reactionId) {
                this.reactionsChanged$.next(resource);
              } else if (resource.action) {
                this.typingChanged$.next(resource);
              }
            })
          );

          const deleted$ = this.chat
            .notifications()
            .asObservable({ subscription: subscription })
            .pipe(
              filter(publication => publication.method === 'DELETE'),
              map(publication => publication.subject.split('/')?.pop())
            );

          this.subSubjects.add(
            deleted$.subscribe((resourceId: string) => {
              if (resourceId) {
                this.reactionsDeleted$.next(resourceId);
              }
            })
          );
          this.chatLoggingService.info({
            logMessage: `chat-cloudlink.service.subscribeSubresources(): ${this.transport} subresources`
          });
        }
      })
      .catch(ex => {
        this.subscriptionError$.next('subresources');
        this.chatLoggingService.error({
          logMessage: `chat-cloudlink.service.subscribeSubresources(): ${this.transport}.error`,
          logParameters: [ex]
        });
      });

    defer(() => {
      this.refreshServiceRefs();
      return this.admin.notifications().beginSubscribe().appendResource({ type: 'users' }).endSubscribe({
        transport: this.transport,
        methodFilter: '^PUT$',
        expiryDateTime: this.getSubscriptionExpiry()
      });
    })
      .pipe(
        retryWhen(
          SharedHelpers.genericRetryStrategy(
            '[chat-cloudlink.service.subscribeToChatNotifySubscriptions()] - users',
            this.chatLoggingService
          )
        ),
        takeUntil(this.subUnsubscribe)
      )
      .toPromise()
      .then(subscription => {
        if (subscription) {
          if (this.subIds.indexOf(subscription.subscriptionId) === -1) {
            this.subIds.push(subscription.subscriptionId);
          }

          const changed$ = this.admin
            .notifications()
            .asObservable({ resource: 'users' })
            .pipe(
              filter(publication => publication.method !== 'DELETE'),
              map(publication => publication.content)
            );

          this.subSubjects.add(
            changed$.subscribe(userEvent => {
              if (userEvent && userEvent.new) {
                this.usersChanged$.next(userEvent.new);
              }
            })
          );
          this.chatLoggingService.info({
            logMessage: `chat-cloudlink.service.subscribeUsers(): ${this.transport} users`
          });
        }
      })
      .catch(ex => {
        this.subscriptionError$.next('users');
        this.chatLoggingService.error({
          logMessage: `chat-cloudlink.service.subscribeUsers(): ${this.transport}.error`,
          logParameters: [ex]
        });
      });

    defer(() => {
      this.refreshServiceRefs();
      return this.admin.notifications().beginSubscribe().appendResource({ type: 'contacts' }).endSubscribe({
        transport: this.transport,
        expiryDateTime: this.getSubscriptionExpiry()
      });
    })
      .pipe(
        retryWhen(
          SharedHelpers.genericRetryStrategy(
            '[chat-cloudlink.service.subscribeToChatNotifySubscriptions()] - contacts',
            this.chatLoggingService
          )
        ),
        takeUntil(this.subUnsubscribe)
      )
      .toPromise()
      .then(subscription => {
        if (subscription) {
          if (this.subIds.indexOf(subscription.subscriptionId) === -1) {
            this.subIds.push(subscription.subscriptionId);
          }

          const changed$ = this.admin
            .notifications()
            .asObservable({ resource: 'contacts' })
            .pipe(
              filter(publication => publication.method !== 'DELETE'),
              map(publication => publication.content)
            );

          this.subSubjects.add(
            changed$.subscribe(contactEvent => {
              if (contactEvent && contactEvent.new) {
                this.contactsChanged$.next(contactEvent.new);
              }
            })
          );

          const deleted$ = this.admin
            .notifications()
            .asObservable({ resource: 'contacts' })
            .pipe(
              filter(publication => publication.method === 'DELETE'),
              map(publication => publication.subject.split('/')?.pop())
            );

          this.subSubjects.add(
            deleted$.subscribe((contactEvent: any) => {
              if (contactEvent) {
                this.contactsChanged$.next({ contactId: contactEvent } as any);
              }
            })
          );
          this.chatLoggingService.info({
            logMessage: `chat-cloudlink.service.subscribeContacts(): ${this.transport} contacts`
          });
        }
      })
      .catch(ex => {
        this.subscriptionError$.next('contacts');
        this.chatLoggingService.error({
          logMessage: `chat-cloudlink.service.subscribeContacts(): ${this.transport}.error`,
          logParameters: [ex]
        });
      });
  }

  private async deleteDatabaseMessage(messageId: string) {
    try {
      await this.chatDatabaseService.deleteMessage(this.accountId, messageId);
    } catch (ex) {
      this.chatLoggingService.error({
        logMessage: 'chat-cloudlink.service.deleteDatabaseMessage(): Error deleting message from db',
        logParameters: [messageId, ex]
      });
    }
  }

  private async refreshSubscriptionsExpiries(isIntervalRefresh = true) {
    this.chatLoggingService.info({
      logMessage: 'chat-cloudlink.service.refreshSubscriptionsExpiries()',
      logParameters: [isIntervalRefresh]
    });
    var filter = '';
    this.subIds.forEach(id => {
      if (filter) {
        filter += ' or ';
      }
      filter += "subscriptionId eq '" + id + "'";
    });
    var refreshedSubs = true;
    if (filter) {
      const resSubs = await this.notify.getSubscriptions({
        skip: 0,
        top: 100,
        filter
      });
      let subs = resSubs.body;
      if (subs.count === this.subIds.length) {
        if (!isIntervalRefresh && subs._embedded && subs._embedded.items && subs._embedded.items.length) {
          let firstSub = subs._embedded.items[0];
          if (firstSub.expiryDateTime) {
            let nowTime = new Date().getTime();
            let expiryTime = new Date(firstSub.expiryDateTime).getTime();
            this.chatLoggingService.info({
              logMessage: 'chat-cloudlink.service.refreshSubscriptionsExpiries(): Difference',
              logParameters: [firstSub.expiryDateTime, expiryTime - nowTime]
            });
            //We'll check if the expiry time is coming up within 13 hours to be safe (to allow for one failed refresh interval)
            if (expiryTime - nowTime > 13 * 60 * 60 * 1000) {
              this.chatLoggingService.info({
                logMessage: 'chat-cloudlink.service.refreshSubscriptionsExpiries(), no need to refresh'
              });
              return;
            }
          }
        }
        for (const s of subs._embedded.items) {
          try {
            await defer(() =>
              this.notify.updateSubscription({
                subscriptionId: s.subscriptionId,
                body: { expiryDateTime: this.getSubscriptionExpiry() }
              })
            )
              .pipe(
                retryWhen(
                  SharedHelpers.genericRetryStrategy(
                    '[chat-cloudlink.service.updateSubscription()]',
                    this.chatLoggingService
                  )
                )
              )
              .toPromise();
          } catch (ex) {
            refreshedSubs = false;
            this.chatLoggingService.error({
              logMessage: 'chat-cloudlink.service.refreshSubscriptionsExpiries() error',
              logParameters: [s.subscriptionId, ex]
            });
          }
        }
      } else {
        refreshedSubs = false;
      }
    } else {
      refreshedSubs = false;
    }
    if (!refreshedSubs) {
      this.chatLoggingService.info({
        logMessage:
          'chat-cloudlink.service.refreshSubscriptionsExpiries() No refreshed subs, subscribe again and reload conversations'
      });
      this.subscribeToSubs();
      this.reloadConversation$.next(true);
    }
  }

  public getSubscriptionExpiry(expiryTime: number | undefined = undefined): Date {
    if (!expiryTime) {
      expiryTime = this.subscriptionExpiryTime;
    }
    const subscriptionExpiry = new Date();
    subscriptionExpiry.setMinutes(subscriptionExpiry.getMinutes() + expiryTime / 60000);
    return subscriptionExpiry;
  }

  private getIosVersion() {
    this.chatLoggingService.info({
      logMessage: 'chat-cloudlink.service.getIosVersion() navigator.platform:',
      logParameters: [navigator.platform]
    });
    if (/iP(hone|od|ad)/.test(navigator.platform)) {
      this.chatLoggingService.info({
        logMessage: 'chat-cloudlink.service.getIosVersion() navigator.appVersion:',
        logParameters: [navigator.appVersion]
      });
      var v = navigator.appVersion.match(/OS (\d+)_(\d+)_?(\d+)?/);
      return [parseInt(v[1], 10), parseInt(v[2], 10), parseInt(v[3] || '0', 10)];
    }
    return null;
  }
}
